import clsx from "clsx";
import { InputHTMLAttributes, useRef } from "react";
import { FieldError } from "../fieldError/FieldError";
import { Icon } from "../icon/Icon";
import * as styles from "./InputField.css";

export type InputProps = {
  error?: string | boolean;
  /** Height/type step; `sm` matches a `sm` select in the same row. */
  size?: keyof typeof styles.inputFieldSizes;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "size">;

/**
 * The native (unpatched) `value` setter for an input element, found by
 * walking the prototype chain from the instance's own prototype — React
 * shadows `value` with a per-instance property to track "did this change",
 * so grabbing the setter this way (rather than assigning `input.value =`
 * directly) is what makes the dispatched "input" event below register as a
 * real change instead of a no-op.
 */
function nativeValueSetter(
  input: HTMLInputElement
): ((value: string) => void) | undefined {
  let proto: object | null = Object.getPrototypeOf(input);
  while (proto) {
    const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
    if (setter) return setter.bind(input);
    proto = Object.getPrototypeOf(proto);
  }
  return undefined;
}

/** Clamps a number-field step to the input's `min`/`max`, defaulting to a step of 1. */
function nextNumberValue(
  value: InputProps["value"],
  direction: 1 | -1,
  min: InputProps["min"],
  max: InputProps["max"],
  step: InputProps["step"]
): string {
  const current = Number(value) || 0;
  const amount = Number(step) || 1;
  let next = current + direction * amount;
  if (min !== undefined && min !== "" && next < Number(min)) next = Number(min);
  if (max !== undefined && max !== "" && next > Number(max)) next = Number(max);
  return String(next);
}

export function InputField({
  value = "",
  error,
  disabled,
  className,
  type,
  min,
  max,
  step,
  size = "md",
  onChange,
  ...rest
}: InputProps) {
  const isFilled =
    typeof value === "number" ||
    (typeof value === "string" && value.trim().length > 0);
  const isNumber = type === "number";
  const inputRef = useRef<HTMLInputElement>(null);

  const inputElement = (
    <input
      {...rest}
      ref={inputRef}
      type={type}
      min={min}
      max={max}
      step={step}
      value={value}
      disabled={disabled}
      onChange={onChange}
      className={clsx(
        styles.inputFieldBase,
        styles.inputFieldSizes[size],
        isNumber && styles.inputFieldNumber,
        isFilled ? styles.inputFieldFilled : styles.inputFieldUnfilled,
        className,
        error && "inputError"
      )}
    />
  );

  if (!isNumber) {
    return <FieldError error={error}>{inputElement}</FieldError>;
  }

  // Sets the value through the native setter (bypassing React's tracked-value
  // shadowing) and dispatches a real "input" event, so React delivers a
  // genuine ChangeEvent from the actual input element and any blur-commit
  // logic downstream keeps working.
  function handleStep(direction: 1 | -1) {
    const input = inputRef.current;
    if (!input) return;
    const nextValue = nextNumberValue(value, direction, min, max, step);
    input.focus();
    nativeValueSetter(input)?.(nextValue);
    input.dispatchEvent(new Event("input", { bubbles: true }));
  }

  return (
    <FieldError error={error}>
      <div className={styles.numberFieldWrapper}>
        {inputElement}
        <div className={styles.stepperGroup}>
          <button
            type="button"
            tabIndex={-1}
            aria-label="Increment"
            disabled={disabled}
            className={styles.stepperButton}
            onClick={() => handleStep(1)}
          >
            <Icon name="chevron-up" />
          </button>
          <button
            type="button"
            tabIndex={-1}
            aria-label="Decrement"
            disabled={disabled}
            className={styles.stepperButton}
            onClick={() => handleStep(-1)}
          >
            <Icon name="chevron-down" />
          </button>
        </div>
      </div>
    </FieldError>
  );
}
